import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  Popover,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Assessment as AssessmentIcon,
  ExpandLess,
  ExpandMore,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  Event as EventIcon,
  ChevronLeft,
  ChevronRight,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import Footer from './Footer';

const drawerWidth = 240;
const drawerWidthCollapsed = 72;
const LOGO_URL = 'https://www.showtimekayaking.com.au/wp-content/uploads/2023/09/New-Showtime-Menu-Logo-1024x279.png';
const FAVICON_URL = 'https://www.showtimekayaking.com.au/wp-content/uploads/2024/02/cropped-SK-Favicon1x1-32x32.png';
const SIDEBAR_STORAGE_KEY = 'backlot-sidebar-collapsed';

/** Sidebar groups: only the section containing the current path should start expanded. */
function expandedMenusForPath(pathname) {
  return {
    operations:
      pathname.startsWith('/manifest') || pathname === '/tours' || pathname.startsWith('/tours/'),
    marketing: pathname.startsWith('/sms') || pathname.startsWith('/email'),
    crm: pathname.startsWith('/guests'),
    reports: pathname.startsWith('/reports') || pathname.startsWith('/graphs'),
    settings: pathname.startsWith('/settings') || pathname.startsWith('/operations/tours'),
  };
}

const Layout = ({ children }) => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) ?? 'false');
    } catch {
      return false;
    }
  });
  const [groupPopover, setGroupPopover] = useState({ anchor: null, item: null });
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  
  const [expandedMenus, setExpandedMenus] = useState(() =>
    expandedMenusForPath(
      typeof window !== 'undefined' ? window.location.pathname : '/',
    ),
  );

  useEffect(() => {
    setExpandedMenus(expandedMenusForPath(location.pathname));
  }, [location.pathname]);

  const effectiveDrawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidth;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(sidebarCollapsed));
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => !prev);
    setGroupPopover({ anchor: null, item: null });
  };

  const handleGroupClick = (item) => {
    if (sidebarCollapsed) {
      return (e) => {
        setGroupPopover({ anchor: e.currentTarget, item });
      };
    }
    return () => handleMenuToggle(item.key);
  };

  const handleGroupChildClick = (path, menuKey) => {
    navigate(path);
    setMobileOpen(false);
    setGroupPopover({ anchor: null, item: null });
    setExpandedMenus((prev) => ({ ...prev, [menuKey]: true }));
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMenuToggle = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentActive = (children) => {
    return children.some((child) => isPathActive(child.path));
  };

  // Define menu structure
  const menuStructure = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', type: 'item' },
    {
      text: 'Operations',
      icon: <BusinessIcon />,
      key: 'operations',
      type: 'group',
      children: [
        ...(hasRole(['superadmin', 'admin'])
          ? [{ text: 'Manifest', icon: <DescriptionIcon />, path: '/manifest' }]
          : []),
        ...(hasRole(['superadmin', 'admin', 'user'])
          ? [{ text: 'Tours', icon: <EventIcon />, path: '/tours' }]
          : []),
      ],
    },
    {
      text: 'Marketing',
      icon: <CampaignIcon />,
      key: 'marketing',
      type: 'group',
      children: [
        ...(hasRole(['superadmin', 'admin'])
          ? [
              { text: 'SMS', icon: <SmsIcon />, path: '/sms' },
              { text: 'Email', icon: <EmailIcon />, path: '/email' },
            ]
          : []),
      ],
    },
    {
      text: 'Reports',
      icon: <AssessmentIcon />,
      key: 'reports',
      type: 'group',
      children: [
        ...(hasRole(['superadmin', 'admin'])
          ? [{ text: 'Historical Data', icon: <AssessmentIcon />, path: '/reports' }]
          : []),
        ...(hasRole(['superadmin', 'admin', 'user'])
          ? [{ text: 'Graphs', icon: <AssessmentIcon />, path: '/graphs' }]
          : []),
      ],
    },
    {
      text: 'CRM',
      icon: <PeopleIcon />,
      key: 'crm',
      type: 'group',
      children: [
        ...(hasRole(['superadmin', 'admin', 'user'])
          ? [{ text: 'Guests', icon: <PeopleIcon />, path: '/guests' }]
          : []),
      ],
    },
    ...(hasRole(['superadmin', 'admin'])
      ? [
          {
            text: 'Settings',
            icon: <SettingsIcon />,
            key: 'settings',
            type: 'group',
            children: [
              { text: 'Users', icon: <PeopleIcon />, path: '/settings/users' },
              {
                text: 'Bookeo product tours',
                icon: <BusinessIcon />,
                path: '/operations/tours',
              },
              { text: 'Changelog', icon: <HistoryIcon />, path: '/settings/changelog' },
            ],
          },
        ]
      : []),
  ];

  const isCollapsed = sidebarCollapsed && !mobileOpen;

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar
        sx={{
          minHeight: 64,
          px: isCollapsed ? 1.5 : 2,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
        }}
      >
        {isCollapsed ? (
          <Box
            component="img"
            src={FAVICON_URL}
            alt="Showtime Kayaking"
            sx={{ width: 40, height: 40, objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="img"
            src={LOGO_URL}
            alt="Showtime Kayaking"
            sx={{ height: 36, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
          />
        )}
      </Toolbar>
      <Divider />
      <List
        sx={{
          px: 1,
          py: 1,
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: { sm: 'calc(100vh - 64px - 56px)' },
          overflowY: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        {menuStructure.map((item) => {
          if (item.type === 'item') {
            const button = (
              <ListItemButton
                selected={isPathActive(item.path)}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start', px: isCollapsed ? 1 : 2 }}
              >
                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 56 }}>{item.icon}</ListItemIcon>
                {!isCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            );
            return (
              <ListItem key={item.text} disablePadding>
                {isCollapsed ? (
                  <Tooltip title={item.text} placement="right" arrow>
                    {button}
                  </Tooltip>
                ) : (
                  button
                )}
              </ListItem>
            );
          } else if (item.type === 'group' && item.children && item.children.length > 0) {
            const isExpanded = Boolean(expandedMenus[item.key]);
            const isActive = isParentActive(item.children);

            const groupButton = (
              <ListItemButton
                onClick={handleGroupClick(item)}
                selected={isActive}
                sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start', px: isCollapsed ? 1 : 2 }}
              >
                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 56 }}>{item.icon}</ListItemIcon>
                {!isCollapsed && <ListItemText primary={item.text} />}
                {!isCollapsed && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            );

            return (
              <React.Fragment key={item.key}>
                <ListItem disablePadding>
                  {isCollapsed ? (
                    <Tooltip title={item.text} placement="right" arrow>
                      {groupButton}
                    </Tooltip>
                  ) : (
                    groupButton
                  )}
                </ListItem>
                {!isCollapsed && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => (
                        <ListItem key={child.text} disablePadding>
                          <ListItemButton
                            selected={isPathActive(child.path)}
                            onClick={() => {
                              navigate(child.path);
                              setMobileOpen(false);
                              setExpandedMenus((prev) => ({ ...prev, [item.key]: true }));
                            }}
                            sx={{ pl: 4 }}
                          >
                            <ListItemIcon>{child.icon}</ListItemIcon>
                            <ListItemText primary={child.text} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          }
          return null;
        })}
      </List>
      {!mobileOpen && (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
          <Tooltip title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton onClick={handleSidebarToggle} size="small" sx={{ width: '100%' }}>
              {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color="default"
        sx={{
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
          transition: 'width 0.2s ease, margin 0.2s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Backlot Admin Portal
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ThemeToggle />
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.email}
            </Typography>
            <IconButton onClick={handleMenuOpen} size="small">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.email?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => navigate('/2fa-setup')}>
                <ListItemIcon>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>2FA Settings</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
            <Popover
              open={Boolean(groupPopover.anchor)}
              anchorEl={groupPopover.anchor}
              onClose={() => setGroupPopover({ anchor: null, item: null })}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <List sx={{ minWidth: 180, py: 0 }}>
                {groupPopover.item?.children?.map((child) => (
                  <ListItem key={child.text} disablePadding>
                    <ListItemButton
                      selected={isPathActive(child.path)}
                      onClick={() => handleGroupChildClick(child.path, groupPopover.item.key)}
                    >
                      <ListItemIcon>{child.icon}</ListItemIcon>
                      <ListItemText primary={child.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Popover>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: effectiveDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: effectiveDrawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: effectiveDrawerWidth,
              overflowX: 'hidden',
              height: '100vh',
              top: 0,
              left: 0,
              position: 'fixed',
              zIndex: theme.zIndex.drawer,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: 'width 0.2s ease',
        }}
      >
        <Toolbar />
        <Box sx={{ flex: 1 }}>
          {children}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;

